// @flow
import orderBy from 'lodash/fp/orderBy';

import type { Catalog, CatalogDates } from './catalog';
import type { ROE } from './type';

import { isLive } from 'modules/catalog-helper';
import { toDate } from 'modules/date';

export type CustomCatalog = $ReadOnly<{|
  _id: string,
  name: string,
  catalogKey: string,
  updatedAt: string,
  sharedWithMeBy: string | null,
  totalCopies?: number,
|}>;

/**
 * The "regular" Collection is attached to the user and gets sent to scramble4
 * via the session validate request. It gets serialized whereas the dashboard widget custom
 * catalog data  is send to scramble4 via another requests and the data get's passed in
 * unserialized to the widget grid e.g. https://ortovox.beta.elasticsuite.com/api/custom_catalogs/
 */
export type SerializedCustomCatalog = $ReadOnly<{|
  _id: string,
  name: string,
  item_count: number,
  parent_catalog_key: string,
  parent_catalog_name: string,
  parent_catalog_dates: CatalogDates,
  created_at: string,
  updated_at: string,
  total_copies?: number,
  author_id: string,
  autogenerated: boolean,
  catalog_division: string | null,
  description: string | null,
  item_count: number,
  shared_with_me_by: string | null,
|}>;

export type CustomCatalogsQueryResponse = ROE<{
  range: string,
  body: $ReadOnlyArray<SerializedCustomCatalog>,
}>;

export type CustomCatalogAsCatalog = $ReadOnly<{|
  ...Catalog,
  ...CustomCatalog,
  isCustomCatalog: true,
|}>;

export const baseCatalogOfCustomCatalog = (
  catalogs: $ReadOnlyArray<Catalog>,
  cc: CustomCatalog | SerializedCustomCatalog,
): Catalog | null =>
  catalogs.find(
    (catalog) =>
      catalog.key === cc.catalogKey || catalog.key === cc.parent_catalog_key,
  ) || null;

export const coerceIntoCustomCatalogAsCatalog = (
  customCatalog: CustomCatalog,
  catalogs: $ReadOnlyArray<Catalog>,
): CustomCatalogAsCatalog | null => {
  const baseCatalog =
    baseCatalogOfCustomCatalog(catalogs, customCatalog) || catalogs[0];

  if (!baseCatalog) return null;

  const abstractCatalogWithCatalogKey = {
    ...baseCatalog,
    ...customCatalog,
    key: customCatalog.catalogKey,
    isCustomCatalog: true,
  };

  const abstractCatalog = abstractCatalogWithCatalogKey;

  return abstractCatalog;
};

export const filteredByUnderlyingCatalogIsLive = (
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
  catalogs: $ReadOnlyArray<Catalog>,
): $ReadOnlyArray<CustomCatalog> =>
  customCatalogs.filter((cc) => {
    const baseCatalog = baseCatalogOfCustomCatalog(catalogs, cc);
    if (!baseCatalog) return null;
    return isLive(baseCatalog);
  });

const byUpdatedAt = (customCatalog: CustomCatalog) =>
  toDate(customCatalog.updatedAt).valueOf();

export const sortedCustomCatalogsByUpdatedAt = (
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
): $ReadOnlyArray<CustomCatalog> =>
  orderBy([byUpdatedAt])(['desc'])(customCatalogs);

export const coerceAllIntoCustomCatalogAsCatalog = (
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
  catalogs: $ReadOnlyArray<Catalog>,
): $ReadOnlyArray<CustomCatalogAsCatalog> =>
  customCatalogs
    .map((customCatalog) =>
      coerceIntoCustomCatalogAsCatalog(customCatalog, catalogs),
    )
    .filter(Boolean);

/**
 * Predicate: Has the `SerializedCustomCatalog` been shared with me?
 *
 * shared_with_me_by is a field that comes on a custom catalog from customCatalogsQuery
 * this field indicates a custom catalog has been shared by another user if it's a string,
 * otherwise shared_with_me_by will be null
 */
export const isSharedWithMe = (cc: CustomCatalog): boolean =>
  typeof cc.sharedWithMeBy === 'string';

/**
 * Predicate: Has the `SerializedCustomCatalog` been copied to other customers?
 *
 * shared_with_me_by is a field that comes on a custom catalog from customCatalogsQuery
 * this field indicates a custom catalog has been shared by another user if it's a string,
 * otherwise shared_with_me_by will be null
 */
export const isSharedWithOthers = (cc: CustomCatalog): boolean =>
  cc.totalCopies !== undefined && cc.totalCopies > 0;

export const deserialized: (SerializedCustomCatalog) => CustomCatalog = ({
  _id,
  name,
  /* eslint-disable camelcase */
  parent_catalog_key,
  updated_at,
  shared_with_me_by,
  total_copies,
  /* eslint-enable camelcase */
}) => ({
  _id,
  name,
  catalogKey: parent_catalog_key,
  updatedAt: updated_at,
  sharedWithMeBy: shared_with_me_by,
  totalCopies: total_copies,
});
